import React, { ComponentType, ReactNode, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { UxContext } from '@client/context/UxContext';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import { getSocketIo } from '@client/store/globalStore/init/socketIo';
import { UserData } from '@client/store/globalStore/state/initialGlobalState';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, ButtonGroup, Container, Divider, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import './Assets/itemList.scss';

import { If } from './If';
import ItemListAutocomplete, { ListItem } from './ItemListAutocomplete';
import RespButton from './RespButton';
import TitleAndDescription from './TitleAndDescription';

const GridButtonContainer = styled(Grid)(({ theme }) => ({
    position: 'sticky',
    top: theme.mixins.toolbar.minHeight as number,
    background: theme.palette.background.default,
    zIndex: 1000,
    marginBottom: '10px'
}));

interface BaseItem {
    id: string;
    name: string;
    locked?: unknown;
    linked?: unknown;
}

export type EnrichedItem<T> = T & {
    lockedReason?: string | false;
    linkedReason?: string | false;
    optionChart?: unknown;
    additionalItemListInfo?: ReactNode;
    nameLineExtra?: ReactNode;
};

export interface ListComponentProps<T> {
    itemList: T[];
    onChange: (item: T | null) => void;
    onView: (item: T) => void;
    onEdit: (item: T) => void;
    onDelete: (item: T) => void;
    onDuplicate: (item: T) => void;
    allowDuplicate?: boolean;
    userData: UserData;
}

interface ItemListProps<T extends BaseItem> {
    chart?: (item: T) => unknown;
    isLocked?: (item: T) => string | false;
    isLinked?: (item: T) => string | false;
    additionalItemListInfo?: (item: T) => ReactNode;
    nameLineExtra?: (item: T) => ReactNode;
    allowDuplicate?: boolean;
    showEditButton?: boolean;
    ListComponent?: ComponentType<ListComponentProps<EnrichedItem<T>>>;
    addNewDisabled?: boolean;
    filterItems?: (item: T) => boolean;
    groupBy?: (item: T) => string;
    onCopyItem?: (item: EnrichedItem<T>) => void;
    onLinkItem?: (item: EnrichedItem<T>) => void;
    onDeleteItem?: (item: T) => void;
}

interface DuplicateResponse {
    id?: string;
    success?: string;
    error?: string;
}

function groupItemsBy<T>(items: T[], keyFn: (item: T) => string): { label: string; items: T[] }[] {
    const map = new Map<string, T[]>();
    for (const item of items) {
        const key = keyFn(item);
        const group = map.get(key);
        if (group) {
            group.push(item);
        } else {
            map.set(key, [item]);
        }
    }
    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([label, groupItems]) => ({ label, items: groupItems }));
}

function ItemList<T extends BaseItem>({
    chart,
    isLocked,
    isLinked,
    additionalItemListInfo,
    nameLineExtra,
    allowDuplicate,
    showEditButton,
    ListComponent,
    addNewDisabled,
    filterItems,
    groupBy,
    onCopyItem,
    onLinkItem,
    onDeleteItem
}: ItemListProps<T>) {
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;
    const {
        globalState: { userData }
    } = useGlobalStore();
    const {
        pageState: { pageConfig },
        pageActions
    } = usePageStore();
    const navigate = useNavigate();

    const [selectedItem, setSelectedItem] = React.useState<EnrichedItem<T> | null>(null);
    const [rawItems, setRawItems] = React.useState<T[] | null>(null);

    const getItems = useCallback(async (): Promise<void> => {
        if (!pageConfig.fetchEndPoint) {
            return;
        }

        // there is no form in the main menu
        pageActions.formData.overrideFormData({});

        const rawData = await lediumApi.get<T[]>(`${pageConfig.fetchEndPoint}/`);
        setRawItems(rawData ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageConfig]);

    const itemList = useMemo((): EnrichedItem<T>[] | null => {
        if (!rawItems) {
            return null;
        }
        let data: EnrichedItem<T>[] = (filterItems ? rawItems.filter(filterItems) : rawItems) as EnrichedItem<T>[];
        if (isLocked) {
            data = data.map((item) => ({ ...item, lockedReason: isLocked(item) }));
        }
        if (isLinked) {
            data = data.map((item) => ({ ...item, linkedReason: isLinked(item) }));
        }
        if (chart) {
            data = data.map((item) => ({ ...item, optionChart: chart(item) }));
        }
        if (additionalItemListInfo) {
            data = data.map((item) => ({ ...item, additionalItemListInfo: additionalItemListInfo(item) }));
        }
        if (nameLineExtra) {
            data = data.map((item) => ({ ...item, nameLineExtra: nameLineExtra(item) }));
        }
        return data;
    }, [rawItems, filterItems, isLocked, isLinked, chart, additionalItemListInfo, nameLineExtra]);

    React.useEffect(() => {
        getItems();
        pageActions.api.setLoading(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getItems]);

    React.useEffect(() => {
        if (itemList !== null) {
            pageActions.api.setLoading(false);
        }
        if (itemList !== null && selectedItem !== null) {
            if (!itemList.some((item) => item.id === selectedItem.id)) {
                setSelectedItem(null);
            }
        }
    }, [itemList, selectedItem]);

    React.useEffect(() => {
        const socket = getSocketIo();
        socket.on('integrity_updated', getItems);
        return () => {
            socket.off('integrity_updated', getItems);
        };
    }, [getItems]);

    function renderList(items: EnrichedItem<T>[]) {
        if (ListComponent) {
            if (groupBy) {
                return groupItemsBy(items, groupBy).map(({ label, items: groupItems }) => (
                    <React.Fragment key={label}>
                        <Typography variant="h5" sx={{ mt: 2, mb: 0.5, color: 'text.secondary', fontWeight: 200 }}>
                            {label}
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        <ListComponent
                            itemList={groupItems}
                            onChange={setSelectedItem}
                            onView={onClickView}
                            onEdit={onClickEdit}
                            onDelete={onClickDelete}
                            onDuplicate={onClickDuplicate}
                            allowDuplicate={allowDuplicate}
                            userData={userData}
                        />
                    </React.Fragment>
                ));
            }
            return (
                <ListComponent
                    itemList={items}
                    onChange={setSelectedItem}
                    onView={onClickView}
                    onEdit={onClickEdit}
                    onDelete={onClickDelete}
                    onDuplicate={onClickDuplicate}
                    allowDuplicate={allowDuplicate}
                    userData={userData}
                />
            );
        }
        const autocompleteItems = groupBy ? [...items].sort((a, b) => groupBy(a).localeCompare(groupBy(b))) : items;
        return (
            <ItemListAutocomplete
                itemList={autocompleteItems as ListItem[]}
                setSelectedItem={(item) => setSelectedItem(item as EnrichedItem<T> | null)}
                selectedItem={selectedItem as ListItem}
                onClickEdit={() => selectedItem && onClickEdit(selectedItem)}
                onClickView={() => selectedItem && onClickView(selectedItem)}
                groupBy={groupBy ? (item) => groupBy(item as T) : undefined}
            />
        );
    }

    function onClickAddNew() {
        navigate(`${pageConfig.url}/new`);
    }

    function onClickEdit(item: EnrichedItem<T>): void {
        navigate(`${pageConfig.url}/edit/${item.id}`);
    }

    function onClickView(item: EnrichedItem<T>): void {
        navigate(`${pageConfig.url}/view/${item.id}`);
    }

    function onClickDelete(item: EnrichedItem<T>): void {
        if (onDeleteItem) {
            onDeleteItem(item);
            return;
        }
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: <span>{i18n('do_you_want_to_delete_X', <b>{item.name}</b>)}</span>,
            destructive: true,
            okCallback: async () => {
                const result = await lediumApi.delete(`${pageConfig.apiEndPoint}/${item.id}`);
                if (result) {
                    handleSnackbar({ message: i18n('X_is_successfully_deleted', item.name) as string });
                    getItems();
                } else {
                    handleSnackbar({ message: i18n('error_occurred') as string, severity: 'error' });
                }
            }
        });
    }

    function onClickDuplicate(item: EnrichedItem<T>): void {
        handleAlert({
            title: i18n('are_you_sure') as string,
            message: <span>{i18n('do_you_want_to_duplicate_X', <b>{item.name}</b>)}</span>,
            okCallback: async () => {
                const data = await lediumApi.get<DuplicateResponse>(`${pageConfig.apiEndPoint}/duplicate/${item.id}`);
                if (data?.id) {
                    handleSnackbar({
                        message: data.success || (i18n('X_is_successfully_duplicated', pageConfig.itemName) as string)
                    });
                    getItems();
                } else {
                    handleSnackbar({
                        message: data?.error || (i18n('something_went_wrong') as string),
                        severity: 'error'
                    });
                }
            }
        });
    }

    return (
        <Container>
            <Grid container spacing={3}>
                <TitleAndDescription />

                <Grid item xs={12}>
                    <GridButtonContainer container spacing={3}>
                        {userData?.admin ? (
                            <Grid item xs sx={{ textAlign: 'center' }}>
                                <RespButton variant="contained" onClick={onClickAddNew} startIcon={<AddBoxIcon />} disabled={addNewDisabled}>
                                    {i18n('add_new')}
                                </RespButton>
                            </Grid>
                        ) : null}

                        {(!ListComponent || showEditButton) && (
                            <>
                                {!ListComponent && (
                                    <Grid item xs sx={{ textAlign: 'center' }}>
                                        <RespButton
                                            variant="contained"
                                            color="primary"
                                            disabled={!selectedItem}
                                            onClick={() => selectedItem && onClickView(selectedItem)}
                                            startIcon={<VisibilityIcon />}
                                        >
                                            {i18n('view')}
                                        </RespButton>
                                    </Grid>
                                )}

                                <If condition={!!userData?.admin}>
                                    <Grid item xs sx={{ textAlign: 'center' }}>
                                        <RespButton
                                            variant="contained"
                                            color="primary"
                                            disabled={!selectedItem || !!selectedItem.locked}
                                            onClick={() => selectedItem && onClickEdit(selectedItem)}
                                            startIcon={<EditIcon />}
                                        >
                                            {i18n('edit')}
                                        </RespButton>
                                    </Grid>

                                    {!ListComponent && (
                                        <>
                                            <If condition={!!allowDuplicate}>
                                                <Grid item xs sx={{ textAlign: 'center' }}>
                                                    <RespButton
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={!selectedItem}
                                                        startIcon={<CallSplitIcon />}
                                                        onClick={() => selectedItem && onClickDuplicate(selectedItem)}
                                                    >
                                                        {i18n('duplicate')}
                                                    </RespButton>
                                                </Grid>
                                            </If>

                                            {onCopyItem && (
                                                <Grid item xs sx={{ textAlign: 'center' }}>
                                                    {onLinkItem ? (
                                                        <ButtonGroup
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={!selectedItem}
                                                            disableElevation={!selectedItem}
                                                            sx={{ margin: 1 }}
                                                        >
                                                            <Button startIcon={<CallSplitIcon />} onClick={() => selectedItem && onCopyItem(selectedItem)}>
                                                                {i18n('copy')}
                                                            </Button>
                                                            <Button startIcon={<LinkIcon />} onClick={() => selectedItem && onLinkItem(selectedItem)}>
                                                                {i18n('link')}
                                                            </Button>
                                                        </ButtonGroup>
                                                    ) : (
                                                        <RespButton
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={!selectedItem}
                                                            startIcon={<CallSplitIcon />}
                                                            onClick={() => selectedItem && onCopyItem(selectedItem)}
                                                        >
                                                            {i18n('copy')}
                                                        </RespButton>
                                                    )}
                                                </Grid>
                                            )}

                                            <Grid item xs sx={{ textAlign: 'center' }}>
                                                <RespButton
                                                    variant="contained"
                                                    color="secondary"
                                                    disabled={!selectedItem || !!selectedItem.locked || !!selectedItem.linked}
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => selectedItem && onClickDelete(selectedItem)}
                                                >
                                                    {i18n('delete')}
                                                </RespButton>
                                            </Grid>
                                        </>
                                    )}
                                </If>
                            </>
                        )}
                    </GridButtonContainer>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: itemList && (itemList.length || ListComponent) ? 'left' : 'center' }}>
                    {itemList && renderList(itemList)}
                </Grid>
            </Grid>
        </Container>
    );
}

export default ItemList;
